import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Calendar,
  Mail,
  Phone,
  User,
  MessageSquare,
  CheckCircle,
  ChevronRight,
  FileText,
  Mic,
  Video,
} from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TIER_ICONS = {
  schriftlich: FileText,
  audio: Mic,
  live: Video,
};

const inputClass =
  "w-full bg-transparent border-b border-[#1A1A2E] text-[#F0EDE8] focus:border-[#C9A84C] focus:outline-none py-3 text-sm placeholder-[#9A9A9A]/40 transition-colors duration-300";

function FormField({ label, icon, children }) {
  return (
    <div>
      <label className="flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.2em] text-[#C9A84C] mb-2">
        {icon}
        {label}
      </label>
      {children}
    </div>
  );
}

export default function BookingModal({ service, onClose }) {
  const [step, setStep] = useState(service?.pricingTiers ? 1 : 2);
  const [selectedTier, setSelectedTier] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    date: "",
    message: "",
    privacy: false,
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.privacy) {
      setError("Bitte akzeptiere die Datenschutzerklärung.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/bookings`, {
        service_id: service.id,
        service_name: service.name,
        format_type: selectedTier?.id || "",
        format_label: selectedTier?.label || "",
        format_price: selectedTier?.price || "",
        name: form.name,
        email: form.email,
        phone: form.phone,
        date: form.date,
        message: form.message,
      });
      setSuccess(true);
    } catch {
      setError("Ein Fehler ist aufgetreten. Bitte versuche es erneut.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        data-testid="booking-modal"
      >
        <div
          className="absolute inset-0 bg-[#050510]/80 backdrop-blur-sm"
          onClick={onClose}
        />
        <motion.div
          className="relative w-full max-w-lg bg-[#0D0D1A] border border-[#1A1A2E] rounded-2xl p-8 z-10 max-h-[90vh] overflow-y-auto"
          initial={{ opacity: 0, y: 50, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 50, scale: 0.95 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          <button
            onClick={onClose}
            className="absolute top-6 right-6 text-[#9A9A9A] hover:text-[#F0EDE8] transition-colors"
            data-testid="booking-modal-close"
          >
            <X size={20} />
          </button>

          {/* Header */}
          <div className="mb-6">
            <p className="text-[0.65rem] uppercase tracking-[0.3em] text-[#C9A84C] mb-1">
              Buchung
            </p>
            <h3
              className="text-2xl font-light text-[#F0EDE8]"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              {service?.name}
            </h3>
          </div>

          {success ? (
            <SuccessScreen onClose={onClose} />
          ) : step === 1 ? (
            <FormatStep
              service={service}
              selectedTier={selectedTier}
              onSelect={(tier) => {
                setSelectedTier(tier);
                setStep(2);
              }}
            />
          ) : (
            <FormStep
              form={form}
              set={set}
              setForm={setForm}
              selectedTier={selectedTier}
              hasTiers={!!service?.pricingTiers}
              onBack={() => { setStep(1); setError(""); }}
              loading={loading}
              error={error}
              onSubmit={handleSubmit}
              isTier={service?.tierNote}
            />
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function FormatStep({ service, selectedTier, onSelect }) {
  return (
    <div>
      <p className="text-[#9A9A9A] text-sm mb-6">
        Bitte wähle dein gewünschtes Format:
      </p>
      <div className="space-y-3">
        {service.pricingTiers.map((tier) => {
          const Icon = TIER_ICONS[tier.id] || FileText;
          return (
            <motion.button
              key={tier.id}
              onClick={() => onSelect(tier)}
              className="w-full flex items-center justify-between p-5 border border-[#1A1A2E] rounded-xl hover:border-[#C9A84C]/60 transition-all duration-300 group text-left"
              whileHover={{ scale: 1.01 }}
              data-testid={`tier-${tier.id}`}
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full border border-[#1A1A2E] group-hover:border-[#C9A84C]/40 flex items-center justify-center text-[#C9A84C]/60 group-hover:text-[#C9A84C] transition-colors">
                  <Icon size={16} />
                </div>
                <span
                  className="text-[#F0EDE8] font-light"
                  style={{ fontFamily: "Cormorant Garamond, serif" }}
                >
                  {tier.label}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span
                  className="text-[#C9A84C] text-xl font-light"
                  style={{ fontFamily: "Cormorant Garamond, serif" }}
                >
                  {tier.price}
                </span>
                <ChevronRight
                  size={16}
                  className="text-[#9A9A9A] group-hover:text-[#C9A84C] transition-colors"
                />
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}

function FormStep({
  form, set, setForm, selectedTier, hasTiers,
  onBack, loading, error, onSubmit, isTier,
}) {
  return (
    <form onSubmit={onSubmit} className="space-y-5">
      {selectedTier && (
        <div className="flex items-center justify-between bg-[#C9A84C]/8 border border-[#C9A84C]/20 rounded-xl px-4 py-3 mb-2">
          <span className="text-[#9A9A9A] text-xs">{selectedTier.label}</span>
          <span
            className="text-[#C9A84C] text-lg font-light"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            {selectedTier.price}
          </span>
        </div>
      )}

      <FormField label="Name" icon={<User size={13} />}>
        <input
          type="text" required value={form.name} onChange={set("name")}
          placeholder="Dein vollständiger Name"
          className={inputClass} data-testid="booking-name-input"
        />
      </FormField>

      <FormField label="E-Mail" icon={<Mail size={13} />}>
        <input
          type="email" required value={form.email} onChange={set("email")}
          placeholder="deine@email.de"
          className={inputClass} data-testid="booking-email-input"
        />
      </FormField>

      <FormField label="Telefon / WhatsApp" icon={<Phone size={13} />}>
        <input
          type="tel" required value={form.phone} onChange={set("phone")}
          placeholder="+49 ..."
          className={inputClass} data-testid="booking-phone-input"
        />
      </FormField>

      <FormField label="Wunschtermin" icon={<Calendar size={13} />}>
        <input
          type="date" required value={form.date} onChange={set("date")}
          min={new Date().toISOString().split("T")[0]}
          className={inputClass} style={{ colorScheme: "dark" }}
          data-testid="booking-date-input"
        />
      </FormField>

      <FormField
        label={isTier ? "Tier-Name & Tierart (optional)" : "Nachricht (optional)"}
        icon={<MessageSquare size={13} />}
      >
        <textarea
          value={form.message} onChange={set("message")} rows={3}
          placeholder={
            isTier
              ? "Name, Tierart, Alter deines Tieres & deine Fragen..."
              : "Was möchtest du mir mitteilen?"
          }
          className={`${inputClass} resize-none`}
          data-testid="booking-message-input"
        />
      </FormField>

      <label className="flex items-start gap-3 cursor-pointer group">
        <button
          type="button" role="checkbox" aria-checked={form.privacy}
          onClick={() => setForm((f) => ({ ...f, privacy: !f.privacy }))}
          className={`w-4 h-4 mt-0.5 flex-shrink-0 border flex items-center justify-center transition-all ${
            form.privacy ? "border-[#C9A84C] bg-[#C9A84C]/20" : "border-[#1A1A2E] group-hover:border-[#C9A84C]/50"
          }`}
          data-testid="booking-privacy-checkbox"
        >
          {form.privacy && <div className="w-2 h-2 bg-[#C9A84C]" />}
        </button>
        <span className="text-xs text-[#9A9A9A] leading-relaxed">
          Ich habe die <span className="text-[#C9A84C]">Datenschutzerklärung</span> gelesen und stimme zu.
        </span>
      </label>

      {error && (
        <p className="text-red-400 text-xs" data-testid="booking-error">{error}</p>
      )}

      <div className="flex gap-3">
        {hasTiers && (
          <button
            type="button" onClick={onBack}
            className="flex-1 border border-[#1A1A2E] text-[#9A9A9A] text-xs uppercase tracking-[0.15em] py-4 hover:border-[#C9A84C]/40 hover:text-[#F0EDE8] transition-all duration-300"
          >
            Zurück
          </button>
        )}
        <button
          type="submit" disabled={loading}
          className="flex-1 border border-[#C9A84C] text-[#C9A84C] text-xs uppercase tracking-[0.2em] py-4 hover:bg-[#C9A84C] hover:text-[#050510] transition-all duration-300 disabled:opacity-40 btn-gold-shimmer"
          data-testid="booking-submit-btn"
        >
          {loading ? "Wird gesendet ..." : "Jetzt buchen"}
        </button>
      </div>
    </form>
  );
}

function SuccessScreen({ onClose }) {
  return (
    <motion.div
      className="text-center py-8"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }}
    >
      <CheckCircle size={48} className="text-[#C9A84C] mx-auto mb-6" />
      <h3
        className="text-3xl font-light text-[#F0EDE8] mb-3"
        style={{ fontFamily: "Cormorant Garamond, serif" }}
      >
        Vielen Dank!
      </h3>
      <p className="text-[#9A9A9A] text-sm leading-relaxed mb-3">
        Du erhältst in Kürze eine Bestätigung per E-Mail.
      </p>
      <p className="text-[#9A9A9A] text-xs leading-relaxed mb-8">
        Um deine Session optimal vorzubereiten, bitten wir dich, vorab unseren{" "}
        <Link
          to="/fragebogen"
          className="text-[#C9A84C] underline underline-offset-2"
          onClick={onClose}
        >
          Vorbereitungsfragebogen
        </Link>{" "}
        auszufüllen.
      </p>
      <button
        onClick={onClose}
        className="border border-[#C9A84C] text-[#C9A84C] text-xs uppercase tracking-[0.2em] px-8 py-3 hover:bg-[#C9A84C] hover:text-[#050510] transition-all duration-300"
        data-testid="booking-success-close"
      >
        Schließen
      </button>
    </motion.div>
  );
}
